import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Video, CheckCircle, XCircle, Clock, User, Building, Globe,
  FileText, AlertTriangle, Eye, Play, ChevronRight, RefreshCw,
  Loader2, Shield, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminVideoVerificationQueue() {
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchVerifications();
    fetchStats();
  }, []);

  const getAdminToken = () => {
    return localStorage.getItem('adminToken');
  };

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      const response = await axios.get(`${API}/api/admin/video-verification/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifications(response.data.verifications || []);
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
      toast.error('Failed to load verification queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getAdminToken();
      const response = await axios.get(`${API}/api/admin/video-verification/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const openReviewDialog = async (verification) => {
    try {
      const token = getAdminToken();
      const response = await axios.get(
        `${API}/api/admin/video-verification/${verification.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedVerification(response.data);
      setReviewNotes('');
      setReviewDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch verification details:', error);
      toast.error('Failed to load verification details');
    }
  };

  const handleReview = async (action) => {
    if (!selectedVerification) return;

    setSubmitting(true);
    try {
      const token = getAdminToken();
      await axios.post(
        `${API}/api/admin/video-verification/${selectedVerification.verification.id}/review`,
        {
          action,
          review_notes: reviewNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Verification ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setReviewDialogOpen(false);
      fetchVerifications();
      fetchStats();
    } catch (error) {
      console.error('Failed to review verification:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const getAIRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'APPROVE': return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECT': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getMatchPercentageColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Pending Review</p>
                  <p className="text-2xl font-bold text-yellow-700">{stats.stats?.pending_review || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Approved</p>
                  <p className="text-2xl font-bold text-green-700">{stats.stats?.approved || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Rejected</p>
                  <p className="text-2xl font-bold text-red-700">{stats.stats?.rejected || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">AI Auto-Approve</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.ai_recommendations?.approve || 0}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Verification Queue */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Video Verification Queue
            </CardTitle>
            <CardDescription>
              Review seller video ID verifications with AI analysis
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchVerifications}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : verifications.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No pending video verifications to review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {verifications.map((v) => (
                <div
                  key={v.id}
                  className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium">{v.user?.company_name || 'Unknown Company'}</p>
                        <p className="text-sm text-slate-500">{v.user?.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" />
                            {v.user?.country || v.country_code}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {new Date(v.uploaded_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* AI Analysis Summary */}
                      <div className="text-right">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getAIRecommendationColor(v.ai_recommendation)}`}>
                          {v.ai_recommendation === 'APPROVE' && <CheckCircle className="h-3 w-3" />}
                          {v.ai_recommendation === 'REJECT' && <XCircle className="h-3 w-3" />}
                          {v.ai_recommendation === 'MANUAL_REVIEW' && <Eye className="h-3 w-3" />}
                          AI: {v.ai_recommendation}
                        </div>
                        <p className={`text-sm font-medium mt-1 ${getMatchPercentageColor(v.ai_match_percentage)}`}>
                          {v.ai_match_percentage}% Match
                        </p>
                      </div>
                      
                      <Button onClick={() => openReviewDialog(v)} size="sm">
                        <Play className="h-4 w-4 mr-1" />
                        Review
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Review Video Verification
            </DialogTitle>
            <DialogDescription>
              Watch the video and compare AI-extracted data with expected values
            </DialogDescription>
          </DialogHeader>

          {selectedVerification && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                    <Building className="h-8 w-8 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{selectedVerification.user?.company_name}</p>
                    <p className="text-slate-500">{selectedVerification.user?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge>{selectedVerification.user?.country}</Badge>
                      <span className="text-sm text-slate-400">
                        Registered: {new Date(selectedVerification.user?.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Player */}
              <div className="bg-black rounded-lg overflow-hidden">
                {selectedVerification.verification?.video_data && (
                  <video
                    src={`data:video/webm;base64,${selectedVerification.verification.video_data}`}
                    controls
                    className="w-full max-h-[400px]"
                  />
                )}
              </div>

              {/* AI Analysis */}
              <div className="grid grid-cols-2 gap-4">
                {/* Expected Data */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Expected (Registered)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Company:</span>
                      <span className="font-medium">{selectedVerification.ai_analysis_summary?.expected_data?.company_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Primary ID:</span>
                      <span className="font-mono font-medium">{selectedVerification.ai_analysis_summary?.expected_data?.primary_business_id || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Secondary ID:</span>
                      <span className="font-mono font-medium">{selectedVerification.ai_analysis_summary?.expected_data?.secondary_business_id || '-'}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Extracted Data */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      AI Extracted (From Video)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Company:</span>
                      <span className="font-medium">{selectedVerification.ai_analysis_summary?.extracted_data?.company_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Primary ID:</span>
                      <span className="font-mono font-medium">{selectedVerification.ai_analysis_summary?.extracted_data?.primary_id || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Secondary ID:</span>
                      <span className="font-mono font-medium">{selectedVerification.ai_analysis_summary?.extracted_data?.secondary_id || '-'}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* AI Recommendation */}
              <div className={`rounded-lg border p-4 ${getAIRecommendationColor(selectedVerification.ai_analysis_summary?.recommendation)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedVerification.ai_analysis_summary?.recommendation === 'APPROVE' && <CheckCircle className="h-6 w-6" />}
                    {selectedVerification.ai_analysis_summary?.recommendation === 'REJECT' && <XCircle className="h-6 w-6" />}
                    {selectedVerification.ai_analysis_summary?.recommendation === 'MANUAL_REVIEW' && <Eye className="h-6 w-6" />}
                    <div>
                      <p className="font-semibold">AI Recommendation: {selectedVerification.ai_analysis_summary?.recommendation}</p>
                      <p className="text-sm opacity-80">
                        Match Score: {selectedVerification.ai_analysis_summary?.match_percentage}%
                      </p>
                    </div>
                  </div>
                </div>
                
                {selectedVerification.ai_analysis_summary?.discrepancies?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-current/20">
                    <p className="text-sm font-medium">Discrepancies Found:</p>
                    <ul className="list-disc list-inside text-sm mt-1 space-y-1">
                      {selectedVerification.ai_analysis_summary.discrepancies.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Review Notes */}
              <div>
                <label className="text-sm font-medium">Review Notes (optional)</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReview('reject')}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Reject
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleReview('approve')}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
